import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Server, Gavel, Ban, AlertTriangle, Sparkles, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANNER = "https://customer-assets-4nw71qhi.emergentagent.net/job_server-control-panel-14/artifacts/21e49e054d4d544a_image.png";

const TILES = [
  { key: "servers", label: "Servers protected", icon: Server, color: "#8B5CF6" },
  { key: "mod_actions", label: "Mod actions", icon: Gavel, color: "#F59E0B" },
  { key: "global_bans", label: "Global bans", icon: Ban, color: "#EF4444" },
  { key: "warnings", label: "Warnings issued", icon: AlertTriangle, color: "#F59E0B" },
  { key: "ai_flags", label: "AI flags caught", icon: Sparkles, color: "#10B981" },
];

const useCountUp = (target) => {
  const [v, setV] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (target == null) return;
    const start = from.current, t0 = performance.now(), dur = 900;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(Math.round(start + (target - start) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step); else from.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return v;
};

const Tile = ({ t, value, i }) => {
  const n = useCountUp(value);
  return (
    <div
      data-testid={`stat-${t.key}`}
      className="relative border border-white/10 bg-[#0D0E15] p-6 overflow-hidden animate-[fadeUp_.5s_ease_both] hover:border-white/25 transition-colors duration-200"
      style={{ animationDelay: `${i * 80}ms` }}
    >
      <div className="absolute left-0 top-0 h-full w-1" style={{ background: t.color }} />
      <t.icon className="w-5 h-5 mb-4" style={{ color: t.color }} />
      <div data-testid={`stat-${t.key}-value`} className="font-mono text-4xl sm:text-5xl font-bold text-white tabular-nums">
        {value == null ? "—" : n.toLocaleString()}
      </div>
      <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-[#94A3B8]">{t.label}</div>
    </div>
  );
};

export default function Stats() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const load = () => axios.get(`${API}/public/stats`).then(r => { setData(r.data); setErr(false); }).catch(() => setErr(true));
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="stats-page">
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}`}</style>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/" data-testid="stats-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-[#64748B] hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <img src={BANNER} alt="ModNode" className="w-full max-w-3xl mb-10 border border-white/10" />
        <div className="flex flex-wrap items-end gap-4 mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">ModNode Stats</h1>
          <span data-testid="stats-live-badge" className="flex items-center gap-2 mb-2 font-mono text-[10px] tracking-widest uppercase text-[#10B981]">
            <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> Live · refreshes every 30s
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((t, i) => <Tile key={t.key} t={t} i={i} value={data?.[t.key]} />)}
        </div>
        <div className="mt-6 font-mono text-[10px] text-[#64748B]" data-testid="stats-updated">
          {err ? "// Couldn't refresh stats — retrying in 30s." : data ? `// Updated ${new Date(data.updated_at).toLocaleTimeString()}` : "// Loading…"}
        </div>
      </div>
    </div>
  );
}
